import { Injectable, Injector, SecurityContext } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { getTkJwt, getUserFLow, getRtkJwt } from '@store/flow-data/general';
import {
  integrationGetContactFormUrl,
  integrationGetMyVertiFinishIntegrationUrl,
  integrationGetMyVertiStartIntegrationUrl,
  integrationGetPrivacyInformationUrl,
  integrationGetRecoverPasswordUrl,
  integrationGetVertiCommercialPageUrl,
} from '@store/configurator/getIntegrations';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadingService } from '../loading/loading.service';
import { VertiIntegration } from '@models';
import { AnalyticsService } from '../analytics/analytics.service';
import { VertiIntegrationFlow } from '@global/enums';
import { deleteSessionStorage } from '@global/utils';

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private readonly store: Store<AppState>;
  private readonly sanitizer: DomSanitizer;
  private loadingSrv: LoadingService;
  private readonly PROJECT = 'pId';
  private readonly PROJECT_VERSION = 'pv';
  private readonly TOKEN = 'tk';
  private readonly REFRESH_TOKEN = 'rtk';
  private readonly ACTION = 'a';
  private readonly FLOW = 'f';
  private readonly JOURNEY = 'j';
  // Contact center queryParams
  private readonly NAME = 'name';
  private readonly SURNAME = 'surname';
  private readonly EMAIL = 'email_address';
  private readonly PHONE = 'phone';
  private readonly RENEWAL_NUMBER = 'quotation_policy';
  private readonly RISK = 'pr';
  private readonly POLICY_ID = 'pcId';
  private readonly DESTINY = 'dest';
  private readonly POLICY_VERSION = 'pcv';

  private readonly HOME_RISK_TYPE = 'HOME';

  get loadingService(): LoadingService {
    if (!this.loadingSrv) {
      this.loadingSrv = this.injector.get(LoadingService);
    }
    return this.loadingSrv;
  }

  constructor(
    protected readonly injector: Injector,
    protected readonly analyticsService: AnalyticsService
  ) {
    this.store = this.injector.get(Store);
    this.sanitizer = this.injector.get(DomSanitizer);
  }

  /**
   * Redirect to MyVerti once the comeround flow is finished
   */
  integrateWithMyVerti({ a, ptId, pV, pcId, dest, pcv, tk, rtk }: VertiIntegration) {
    combineLatest([
      this.store.pipe(select(integrationGetMyVertiFinishIntegrationUrl)),
      this.store.pipe(select(getTkJwt)),
      this.store.pipe(select(getRtkJwt)),
      this.store.pipe(select(getUserFLow)),
    ])
      .pipe(
        filter(storeData => storeData.every(data => !!data)),
        take(1)
      )
      .subscribe(([integrationUrl, tkJwt, rtkJwt, userFlow]) => {
        const replacedUrl = this.replaceQuerystringValues(integrationUrl, {
          [this.ACTION]: a,
          [this.PROJECT]: ptId,
          [this.FLOW]: userFlow,
          [this.JOURNEY]: this.analyticsService.getJourneyDigitalDataValueByVertiFlow(userFlow as VertiIntegrationFlow),
          [this.TOKEN]: tk ?? tkJwt,
          [this.REFRESH_TOKEN]: rtk ?? rtkJwt,
          [this.PROJECT_VERSION]: pV,
          [this.RISK]: this.HOME_RISK_TYPE,
          [this.POLICY_ID]: pcId,
          [this.DESTINY]: dest,
          [this.POLICY_VERSION]: pcv,
        });

        deleteSessionStorage(this.store);

        this.openUrl(replacedUrl, LinkOpenEnum.SAME_TAB);
        this.loadingService.dismissLoading();
      });
  }

  integrateWithContactForm(contactFormUserData?: ContactFormPrefiled) {
    this.store
      .pipe(select(integrationGetContactFormUrl))
      .pipe(
        filter(url => !!url),
        take(1)
      )
      .subscribe((contactForm: string) => {
        let contactFormUrl = contactForm;
        if (contactFormUserData) {
          const { name, surname, email, mobilePhoneNumber, renewalNumber } = contactFormUserData;
          contactFormUrl = this.replaceQuerystringValues(contactForm, {
            [this.NAME]: name,
            [this.SURNAME]: surname,
            [this.EMAIL]: email,
            [this.PHONE]: mobilePhoneNumber,
            [this.RENEWAL_NUMBER]: renewalNumber,
          });
        }
        deleteSessionStorage(this.store);
        this.openUrl(contactFormUrl, LinkOpenEnum.NEW_TAB);
      });
  }

  phoneCall(phoneNumber: string) {
    window.open(`tel:${phoneNumber}`);
  }

  /**
   * Comeback to MyVerti if user wants to leave comeround flow
   */
  returnToMyVerti({ ptId }: VertiIntegration) {
    combineLatest([
      this.store.pipe(select(integrationGetMyVertiStartIntegrationUrl)),
      this.store.pipe(select(getTkJwt)),
      this.store.pipe(select(getRtkJwt)),
    ])
      .pipe(
        filter(storeData => storeData.every(data => !!data)),
        take(1)
      )
      .subscribe(([integrationUrl, tkJwt, rtkJwt]) => {
        const replacedUrl = this.replaceQuerystringValues(integrationUrl, {
          [this.PROJECT]: ptId,
          [this.TOKEN]: tkJwt,
          [this.REFRESH_TOKEN]: rtkJwt,
        });
        deleteSessionStorage(this.store);
        this.openUrl(replacedUrl, LinkOpenEnum.SAME_TAB);
      });
  }

  recoverPassword() {
    this.store
      .pipe(select(integrationGetRecoverPasswordUrl))
      .pipe(
        filter(url => !!url),
        take(1)
      )
      .subscribe((recoverPasswordUrl: string) => {
        this.openUrl(recoverPasswordUrl, LinkOpenEnum.NEW_TAB);
      });
  }

  openPrivacyInformation() {
    this.store
      .pipe(select(integrationGetPrivacyInformationUrl))
      .pipe(
        filter(url => !!url),
        take(1)
      )
      .subscribe((privacyInformationUrl: string) => {
        this.openUrl(privacyInformationUrl, LinkOpenEnum.NEW_TAB);
      });
  }

  goToVerti() {
    this.store
      .pipe(select(integrationGetVertiCommercialPageUrl))
      .pipe(
        filter(url => !!url),
        take(1)
      )
      .subscribe((vertiCommercialPage: string) => {
        deleteSessionStorage(this.store);
        this.openUrl(vertiCommercialPage, LinkOpenEnum.SAME_TAB);
      });
  }

  openUrl(url: string, target: LinkOpenEnum) {
    const sanitizedUrl = this.sanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      this.sanitizer.bypassSecurityTrustResourceUrl(url)
    );
    // Made twice if one of them fails to redirect
    if (target === LinkOpenEnum.SAME_TAB) {
      window.location.href = sanitizedUrl;
    }
    window.open(sanitizedUrl, target);
  }

  private replaceQuerystringValues(url: string, appendValues: Record<string, string>): string;
  private replaceQuerystringValues(
    url: string,
    appendValues: Record<string, string>,
    replaceValues: Record<string, string>
  ): string;

  private replaceQuerystringValues(
    url: string,
    appendValues: Record<string, string> = {},
    replaceValues?: Record<string, string>
  ): string {
    const appendElement = new URL(url).search.length ? '&' : '?';

    if (!replaceValues) {
      return `${url}${appendElement}${this.objectToQuerystring(appendValues)}`;
    }
    let replacedUrl = url;
    let newReplaceValues = { ...replaceValues };
    const toAppendValues = {};
    Object.keys(appendValues).forEach(key => {
      if (!replacedUrl.includes(key)) {
        toAppendValues[key] = appendValues[key];
      }
    });
    newReplaceValues = { ...newReplaceValues, ...toAppendValues };
    Object.keys(newReplaceValues).forEach(key => {
      if (replacedUrl.includes(key)) {
        replacedUrl = replacedUrl.replace(key, newReplaceValues[key]);
      }
    });
    replacedUrl = `${replacedUrl}${appendElement}${this.objectToQuerystring(toAppendValues)}`;
    return replacedUrl;
  }

  private objectToQuerystring(obj: Record<string, string>): string {
    if (!obj) {
      return '';
    }
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return '';
    }
    return keys
      .filter(key => Boolean(key) && Boolean(obj[key]))
      .map(key => `${key}=${obj[key]}`)
      .join('&');
  }
}

enum LinkOpenEnum {
  NEW_TAB = '_blank',
  SAME_TAB = '_self',
  SYSTEM_BROWSER = '_system',
}

type ContactFormPrefiled = {
  name: string;
  surname: string;
  email: string;
  mobilePhoneNumber: string;
  /** ProjectId | PolicyId (depending on the flow) */
  renewalNumber: string;
};
